import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/feature/SectionHeading";
import Reveal from "@/components/base/Reveal";
import usePageMeta from "@/hooks/usePageMeta";
import { reviews, siteInfo, stats } from "@/mocks/siteData";

const positions = [
  {
    icon: "ri-compass-3-line",
    title: "网站定位",
    desc: "做一个能被当案例库用的翻车事件档案站。不止告诉你怎么翻的，还要说清楚是哪个流程环节没做好。",
  },
  {
    icon: "ri-plant-line",
    title: "发展理念",
    desc: "把每一次公开失误都当成一次行业公开课。复盘优先于嘲讽，事实优先于情绪。",
  },
  {
    icon: "ri-bar-chart-grouped-line",
    title: "内容边界",
    desc: "只整理发生在公开场合、有公开信息可查的事件，不涉及私人生活，也不做人身攻击。",
  },
];

const principles = [
  { icon: "ri-check-double-line", text: "每条事件都以公开回放、平台说明或当事方回应为主要依据，单一来源的说法会标注来源。" },
  { icon: "ri-time-line", text: "按分钟梳理时间线，让读者先看清经过，而不是先看到立场。" },
  { icon: "ri-eye-off-line", text: "站内图片统一使用契合内容的示意画面，不包含当事人真实影像。" },
  { icon: "ri-edit-2-line", text: "收到有依据的纠错后会核对更新，并在原文标注修订说明。" },
  { icon: "ri-user-heart-line", text: "评价指向行为与流程，不针对个人进行人格评判或动机揣测。" },
];

export default function About() {
  const crumbs = [{ label: "首页", to: "/" }, { label: "关于我们" }];

  usePageMeta({
    title: "关于我们_内容原则与品牌介绍 - 直播翻车观察局",
    description:
      "直播翻车观察局是一个专注网红翻车现场的编辑型内容站。这里介绍我们的网站定位、发展理念、内容原则、读者口碑与联系方式。",
    keywords: "直播翻车观察局,关于我们,网红翻车网站,内容原则,翻车事件复盘",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "AboutPage",
          name: "关于直播翻车观察局",
          description: "专注网红翻车现场的编辑型内容站，介绍网站定位、理念与内容原则。",
          inLanguage: "zh-CN",
        },
        {
          "@type": "Organization",
          name: siteInfo.name,
          email: siteInfo.email,
          foundingDate: "2023",
          description: siteInfo.description,
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: crumbs.map((crumb, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: crumb.label,
            item: crumb.to ? `https://fanche-observer.example${crumb.to}` : undefined,
          })),
        },
      ],
    },
  });

  return (
    <SiteLayout>
      <section className="w-full px-4 md:px-8 pt-8 pb-12 md:pb-16 bg-background-100 border-b border-background-200">
        <Breadcrumb items={crumbs} />
        <div className="mt-6 max-w-3xl">
          <p className="flex items-center gap-2 text-xs font-medium tracking-[0.2em] text-primary-600 mb-4 uppercase">
            <span className="w-6 h-px bg-primary-400"></span>
            About us
          </p>
          <h1 className="font-heading font-black text-3xl md:text-5xl leading-tight text-foreground-950">
            关于 {siteInfo.name}
          </h1>
          <p className="mt-5 text-sm md:text-lg leading-relaxed text-foreground-600">
            {siteInfo.tagline}。我们是一群对内容行业有兴趣的编辑，
            从 {siteInfo.since}开始收集网红翻车现场、直播事故与名场面回放，
            并尝试用更克制的方式把它们整理成可复盘的档案。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-50">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
          <div>
            <SectionHeading
              eyebrow="Who we are"
              title="把吃瓜的热情，变成能复用的经验"
              desc="翻车事件之所以值得记录，是因为它几乎总是暴露了同一个问题：流程里有一环没人负责。"
            />
            <div className="space-y-4">
              <p className="text-sm md:text-base leading-relaxed text-foreground-700">
                我们见过太多事件在传播中被简化成一句标签。报价失误变成「割韭菜」，
                忘关麦变成「耍大牌」，人设崩塌变成「演戏」。标签很快，但没什么用。
              </p>
              <p className="text-sm md:text-base leading-relaxed text-foreground-700">
                所以这个站点选了一条相对慢的路：把公开信息按时间排好，把关键画面摆出来，
                再说明白是哪一步出了问题。你可以不同意我们的复盘，但至少你看得见依据。
              </p>
              <p className="text-sm md:text-base leading-relaxed text-foreground-700">
                目前站内收录了 {stats[0].value} 起事件的整理，覆盖直播事故、带货翻车、连线失控、
                言论翻车与人设崩塌五个主要方向。
              </p>
            </div>
          </div>

          <Reveal>
            <div className="relative w-full h-[300px] md:h-[440px] rounded-lg overflow-hidden bg-foreground-950">
              <img
                src="https://readdy.ai/api/search-image?query=quiet%20editorial%20workspace%20at%20night%20with%20a%20glowing%20laptop%2C%20open%20notebooks%2C%20a%20warm%20desk%20lamp%20and%20a%20cup%20of%20tea%20on%20a%20dark%20wooden%20desk%2C%20calm%20warm%20amber%20lighting%2C%20no%20people&width=1200&height=1100&seq=about-studio-01&orientation=portrait"
                alt="直播翻车观察局编辑工作场景示意"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 to-transparent"></div>
              <div className="absolute left-5 bottom-5 right-5">
                <p className="text-xs text-background-200">编辑在核对公开信息后才会发布整理</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-100 border-y border-background-200">
        <SectionHeading eyebrow="Position" title="定位与理念" align="center" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {positions.map((item, index) => (
            <Reveal key={item.title} delay={index * 70} className="h-full">
              <div className="h-full p-6 rounded-lg bg-background-50 border border-background-200">
                <span className="w-11 h-11 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 font-heading font-bold text-lg text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-50">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          <div className="lg:col-span-1">
            <SectionHeading
              eyebrow="Principles"
              title="内容原则"
              desc="这些原则写在编辑部的工作手册里，也决定了哪些事件我们不做。"
            />
            <div className="grid grid-cols-2 gap-4">
              {stats.slice(0, 4).map((item) => (
                <div key={item.label} className="p-4 rounded-lg bg-background-100 border border-background-200">
                  <p className="font-heading font-bold text-2xl text-foreground-950">
                    {item.value}
                    <span className="text-xs font-normal text-foreground-500 ml-1">{item.unit}</span>
                  </p>
                  <p className="mt-1 text-xs text-foreground-500">{item.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2">
            <ul className="space-y-3">
              {principles.map((item, index) => (
                <Reveal key={item.text} delay={index * 50}>
                  <li className="flex items-start gap-4 p-4 md:p-5 rounded-lg bg-background-100 border border-background-200">
                    <span className="shrink-0 w-9 h-9 rounded-md bg-foreground-950 text-background-50 flex items-center justify-center">
                      <i className={item.icon}></i>
                    </span>
                    <p className="text-sm md:text-base leading-relaxed text-foreground-700">{item.text}</p>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-100 border-y border-background-200">
        <SectionHeading
          eyebrow="Readers"
          title="读者口碑"
          desc="来自内容从业者、品牌运营和普通网友的真实反馈。"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {reviews.map((item, index) => (
            <Reveal key={item.name} delay={index * 70} className="h-full">
              <figure className="flex flex-col h-full p-5 md:p-6 rounded-lg bg-background-50 border border-background-200">
                <div className="flex items-center gap-1 text-accent-500 mb-3">
                  {Array.from({ length: 5 }, (_, starIndex) => (
                    <i key={starIndex} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <blockquote className="text-sm leading-relaxed text-foreground-700 flex-1">{item.text}</blockquote>
                <figcaption className="mt-5 pt-4 border-t border-background-200">
                  <span className="block text-sm font-medium text-foreground-950">{item.name}</span>
                  <span className="block text-xs text-foreground-500 mt-0.5">{item.role}</span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-50">
        <div className="rounded-lg bg-foreground-950 p-6 md:p-10 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-10 items-center">
          <div className="lg:col-span-2">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-background-50">想和我们聊聊？</h2>
            <p className="mt-3 text-sm md:text-base leading-relaxed text-background-200 max-w-2xl">
              无论是提供事件线索、指出内容错误，还是想引用站内整理，都可以直接联系我们。
              邮件与留言我们都会看，通常两个工作日内回复。
            </p>
            <div className="mt-5 flex items-center gap-2.5 text-sm text-background-100">
              <span className="w-8 h-8 rounded-md bg-primary-900 flex items-center justify-center">
                <i className="ri-mail-line"></i>
              </span>
              {siteInfo.email}
            </div>
          </div>
          <div className="lg:col-span-1 flex flex-col gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-1.5 h-11 px-5 rounded-md bg-accent-500 text-accent-950 text-sm font-medium whitespace-nowrap hover:bg-accent-400 transition-colors"
            >
              <i className="ri-send-plane-line"></i>
              前往联系页面
            </Link>
            <Link
              to="/category"
              className="inline-flex items-center justify-center gap-1.5 h-11 px-5 rounded-md border border-background-50/25 text-sm text-background-100 whitespace-nowrap hover:border-accent-400 hover:text-accent-400 transition-colors"
            >
              浏览全部事件
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}